import { Box, Divider, Icon, IconButton, Stack, Tag, Text, Tooltip } from '@chakra-ui/react';
import { Cargo, CargoType, RequireOnly, TSRequest } from '@pelicargo/types';
import { HANDLING_OPTIONS, PACKING_OPTIONS, Show } from '@pelicargo/ui';
import { getPrettyCargoType, prettyDate } from '@pelicargo/utils';
import { Prisma } from '@prisma/client';
import { useMemo } from 'react';
import { HiArrowRight } from 'react-icons/hi2';
import { Link } from 'react-router-dom';

import { trpc } from '../../config/trpc';
import { useCargos } from '../../hooks/cargo/useCargos';
import { LinkIcon } from '../LinkIcon';

type RequestListItemProps = {
  request: TSRequest;
  targetRequest: RequireOnly<
    Prisma.RequestGetPayload<{
      include: {
        linked_requests: true;
      };
    }>,
    'id' | 'linked_requests' | 'main_request_id'
  >;
  isDuplicate?: boolean;
  isSimilar?: boolean;
};

const options = [...HANDLING_OPTIONS, ...PACKING_OPTIONS];

export const RequestLinkItem = ({ request, targetRequest, isDuplicate, isSimilar }: RequestListItemProps) => {
  const { mutateAsync: linkRequest, isLoading: isLinking } = trpc.linkRequest.useMutation();
  const { mutateAsync: unlinkRequest, isLoading: isUnlinking } = trpc.unlinkRequest.useMutation();
  const isLoading = isLinking || isUnlinking;

  const linkedRequestIds = useMemo(() => targetRequest?.linked_requests?.map(({ id }) => id) || [], [targetRequest]);
  const isLinked =
    linkedRequestIds?.includes(Number(request?.id)) || targetRequest.main_request_id === Number(request.id);

  const [cargos] = useCargos(Number(request?.id));

  const renderHandlingOption = (option: string | number) => {
    const selected = options.find((opt) => opt.value === option);

    return (
      <Box key={selected?.value} h="full">
        <Tooltip label={selected?.label}>
          <Stack direction="row" align="center">
            <Icon as={selected?.icon} w="20px" h="20px" color="white" />
          </Stack>
        </Tooltip>
      </Box>
    );
  };

  const renderCargo = (cargo: Cargo) => {
    const dimensions = [cargo?.length, cargo?.width, cargo?.height];
    return (
      <Stack key={cargo?.id} direction="row" align="center" justify="space-between">
        <Text>{dimensions.join('x')}</Text>
        <Text>{cargo?.weight}kg</Text>
        <Text>x{cargo?.quantity}</Text>
        <Text>{cargo?.handling?.map(renderHandlingOption)}</Text>
      </Stack>
    );
  };

  const handleClick = async () => {
    const params = { main_request_id: Number(request?.id), linked_request_id: Number(targetRequest?.id) };

    if (isLinked) return await unlinkRequest(params);
    return await linkRequest(params);
  };

  return (
    <Stack key={request?.id} direction="row" p="2" align="center" divider={<Divider orientation="vertical" h="100" />}>
      <Stack as={Link} to={`/requests/${request.id}`} flex="1">
        <Stack direction="row" align="center" justify="space-between">
          <Tag size="sm">{getPrettyCargoType(request?.cargo_type as CargoType)}</Tag>
          <Show if={isDuplicate}>
            <Tag size="sm" colorScheme="blue">
              Duplicate
            </Tag>
          </Show>
          <Show if={isSimilar}>
            <Tag size="sm" colorScheme="yellow">
              Similar
            </Tag>
          </Show>
          <Text textStyle="captionSmallMedium">{prettyDate(new Date(request?.created_at), { showTime: true })}</Text>
        </Stack>
        <Stack direction="row" align="start">
          <Text fontWeight="bold">R{request?.id}</Text>
          <Stack direction="row" align="center" justify="start" spacing={0}>
            <Text fontWeight="400" flexWrap="wrap" maxWidth="100" textAlign="center">
              {request?.origins?.join(', ')}
            </Text>
            <Icon as={HiArrowRight} fontSize="18px" mx="1.5" />
            <Text fontWeight="400" flexWrap="wrap" maxWidth="100" textAlign="center">
              {request?.destinations?.join(', ')}
            </Text>
          </Stack>
        </Stack>
        <Stack>{cargos?.map(renderCargo)}</Stack>
      </Stack>
      <IconButton
        aria-label="Link Request"
        title={isLinked ? `Remove duplicate link` : `Set R${targetRequest?.id} as a duplicate of R${request?.id}`}
        variant="ghost"
        isDisabled={isSimilar}
        icon={<LinkIcon isLinked={isLinked} />}
        isLoading={isLoading}
        onClick={handleClick}
      />
    </Stack>
  );
};
